import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  CircleCheck,
  ExternalLink,
  Loader2,
  Search,
  Receipt,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { formatUsd, markInvoicePaidById } from '@/lib/paymentsData';
import { toast } from 'sonner';

const STATUS_BADGE = {
  paid:
    'bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30',
  overdue:
    'bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30',
  due:
    'bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30',
  upcoming: 'bg-[hsl(var(--muted))] text-foreground border-border',
};

const Row = ({ row, jobs, onChanged, busyKey, setBusyKey, navigate }) => {
  const { t } = useTranslation();
  const handleMark = async () => {
    const job = jobs.find((j) => j.id === row.job_id);
    if (!job) return;
    setBusyKey(row.row_key);
    try {
      const updated = await markInvoicePaidById(job, row.invoice_id);
      toast.success(t('payments.list.markedPaidToast'));
      onChanged?.(updated);
    } catch (e) {
      toast.error(e?.message || t('payments.list.markPaidFail'));
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div
      className="grid grid-cols-12 sm:grid-cols-[minmax(0,2.2fr)_minmax(84px,1fr)_minmax(76px,1fr)_minmax(96px,1fr)_10.5rem] gap-2 items-center px-3 py-3 border-b border-border hover:bg-[hsl(var(--muted))]/30 transition-colors"
      data-testid={`payment-line-${row.row_key}`}
    >
      <div className="col-span-12 sm:col-span-1 min-w-0">
        <div className="text-sm font-semibold text-foreground truncate">
          {row.job_name}
        </div>
        <div className="text-xs text-muted-foreground truncate">
          {row.client || t('payments.list.noClient')} {row.desc ? <span> {'\u00b7'} {row.desc}</span> : null}
        </div>
      </div>
      <div className="col-span-4 sm:col-span-1">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          {t('payments.list.due')}
        </div>
        <div className="text-sm text-foreground">{row.due || t('payments.list.tbd')}</div>
        {row.days_past_due ? (
          <div className="text-[11px] text-[hsl(var(--destructive))]">
            {t('payments.list.pastDue', { count: row.days_past_due })}
          </div>
        ) : null}
      </div>
      <div className="col-span-4 sm:col-span-1">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          {t('payments.list.amount')}
        </div>
        <div className="text-sm font-semibold text-foreground tabular-nums">
          {formatUsd(row.amount)}
        </div>
      </div>
      <div className="col-span-4 sm:col-span-1 flex justify-end sm:justify-start">
        <Badge
          variant="outline"
          className={cn('uppercase text-[10px] tracking-[0.16em]', STATUS_BADGE[row.status])}
        >
          {t(`payments.list.status.${row.status}`, { defaultValue: row.status })}
        </Badge>
      </div>
      <div className="col-span-12 sm:col-span-1 flex items-center justify-end gap-1.5">
        {row.status !== 'paid' ? (
          <Button
            size="sm"
            onClick={handleMark}
            disabled={busyKey === row.row_key}
            className="h-8 bg-[hsl(var(--success))]/20 text-[hsl(var(--success))] border border-[hsl(var(--success))]/40 hover:bg-[hsl(var(--success))]/30 uppercase tracking-[0.16em] text-[10px]"
            data-testid={`payment-mark-paid-${row.row_key}`}
          >
            {busyKey === row.row_key ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <CircleCheck size={12} className="mr-1.5" />
            )}
            {t('payments.list.markPaid')}
          </Button>
        ) : null}
        <Button
          size="icon"
          variant="ghost"
          onClick={() =>
            navigate(`/dashboard/active-jobs?jobId=${encodeURIComponent(row.job_id)}`)
          }
          className="h-8 w-8 text-muted-foreground hover:text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10"
          aria-label={t('payments.list.openJobAria')}
          data-testid={`payment-open-job-${row.row_key}`}
        >
          <ExternalLink size={14} />
        </Button>
      </div>
    </div>
  );
};

const ListSkeleton = () => (
  <div className="divide-y divide-border" data-testid="payments-skeleton">
    {Array.from({ length: 5 }).map((_, i) => (
      <div key={i} className="px-3 py-4 flex items-center gap-3">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-6 w-24 ml-auto" />
      </div>
    ))}
  </div>
);

export const PaymentsHistoryList = ({
  rows,
  loading,
  jobs,
  view,
  search,
  onSearch,
  onChanged,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [busyKey, setBusyKey] = useState(null);
  const viewLabel = t(`payments.list.viewOpts.${view}`, {
    defaultValue: t('payments.list.viewOpts.all'),
  });
  return (
    <Card
      className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]"
      data-testid="payments-history-card"
    >
      <CardContent className="p-3 sm:p-4">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-3">
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              {t('payments.list.viewLabel')}
            </span>
            <Badge
              variant="outline"
              className="uppercase text-[10px] tracking-[0.16em] border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10"
              data-testid="payments-view-label"
            >
              {viewLabel}
            </Badge>
          </div>
          <div className="flex flex-1 items-center gap-2">
            <div className="relative flex-1">
              <Search
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
                size={14}
              />
              <Input
                value={search}
                onChange={(e) => onSearch(e.target.value)}
                placeholder={t('payments.list.searchPlaceholder')}
                className="pl-8 h-9 bg-[hsl(var(--muted))] border-border text-foreground"
                data-testid="payments-search"
              />
            </div>
          </div>
        </div>

        <div className="hidden sm:grid sm:grid-cols-[minmax(0,2.2fr)_minmax(84px,1fr)_minmax(76px,1fr)_minmax(96px,1fr)_10.5rem] gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground border-b border-border">
          <span>{t('payments.list.colJobClient')}</span>
          <span>{t('payments.list.colDue')}</span>
          <span>{t('payments.list.colAmount')}</span>
          <span>{t('payments.list.colStatus')}</span>
          <span className="text-right">{t('payments.list.colActions')}</span>
        </div>

        {loading ? (
          <ListSkeleton />
        ) : rows.length === 0 ? (
          <div
            className="py-16 text-center text-sm text-muted-foreground flex flex-col items-center gap-2"
            data-testid="payments-empty"
          >
            <Receipt size={20} className="text-muted-foreground/70" />
            {t('payments.list.empty')}
          </div>
        ) : (
          <div data-testid="payments-list">
            {rows.map((row) => (
              <Row
                key={row.row_key}
                row={row}
                jobs={jobs}
                navigate={navigate}
                busyKey={busyKey}
                setBusyKey={setBusyKey}
                onChanged={onChanged}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PaymentsHistoryList;
